import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Building2, Plus, Check, ChevronDown, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { dteApi, humanizeError} from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * CompanySwitcher — selector multi-empresa al estilo Slack.
 *
 * Permite a un usuario (típicamente un contador) saltar entre las empresas
 * (NITs) que administra desde el mismo login. Reemite el JWT con el nuevo
 * tenant_id y refresca toda la app.
 *
 * Se monta en el sidebar arriba del nav.
 */
export default function CompanySwitcher() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ nombre: "", nit: "", nrc: "", nombreComercial: "", alias: "" });

  const reload = async () => {
    setLoading(true);
    try {
      const r = await dteApi.companiesList();
      setCompanies(r.items || []);
      const act = (r.items || []).find(c => c.active);
      setActive(act || null);
    } catch (e) {
      // silencioso si endpoint aún no disponible
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (user) reload(); }, [user]);

  const switchTo = async (tenant_id) => {
    if (active?.tenant_id === tenant_id) { setOpen(false); return; }
    try {
      const r = await dteApi.companiesSwitch(tenant_id);
      // Remover flag de ambiente (cada empresa tiene su propio ambiente)
      localStorage.removeItem("dteflow_ambiente_chosen");
      toast.success(`Cambiando a ${r.alias || tenant_id}…`);
      setTimeout(() => window.location.assign("/dashboard"), 350);
    } catch (e) {
      toast.error(humanizeError(e, "Error al cambiar de empresa"));
    }
  };

  const create = async () => {
    const nombreClean = (form.nombre || "").trim();
    if (!nombreClean) { toast.error("Nombre obligatorio"); return; }
    setCreating(true);
    try {
      const r = await dteApi.companiesCreate({ ...form, nombre: nombreClean });
      toast.success(`Empresa "${r.alias || r.nombre}" creada`);
      setShowCreate(false);
      setForm({ nombre: "", nit: "", nrc: "", nombreComercial: "", alias: "" });
      await reload();
      // Auto-switch a la nueva empresa
      switchTo(r.tenant_id);
    } catch (e) {
      console.error("companiesCreate error", e);
      toast.error(humanizeError(e, e?.message || "Error al crear empresa"));
    } finally {
      setCreating(false);
    }
  };

  if (loading || !active) return null;

  const displayName = active.alias || active.emisor?.nombreComercial || active.emisor?.nombre || active.tenant_id;
  const initial = (displayName || "?").charAt(0).toUpperCase();

  return (
    <div className="relative" data-testid="company-switcher">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="company-switcher-trigger w-full flex items-center gap-2 px-2 py-1.5 rounded-lg bg-slate-50 hover:bg-slate-100 ring-1 ring-slate-200 transition group"
        data-testid="company-switcher-btn"
      >
        <div className="w-7 h-7 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0 text-[12px] font-semibold shadow-sm">
          {initial}
        </div>
        <div className="flex-1 min-w-0 text-left">
            <div className="overline text-[8px] mb-0.5">Empresa activa</div>
            <div className="text-[11.5px] font-semibold text-slate-900 truncate leading-tight" data-testid="company-active-name">
            {displayName}
          </div>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute top-full left-0 right-0 mt-1.5 bg-white rounded-xl shadow-2xl ring-1 ring-slate-200 overflow-hidden z-40"
          data-testid="company-switcher-panel"
        >
          <div className="max-h-72 overflow-y-auto">
            {companies.map(c => {
              const name = c.alias || c.emisor?.nombreComercial || c.emisor?.nombre || c.tenant_id;
              return (
                <button
                  key={c.tenant_id}
                  type="button"
                  onClick={() => switchTo(c.tenant_id)}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-slate-50 transition ${c.active ? "bg-emerald-50/50" : ""}`}
                  data-testid={`company-option-${c.tenant_id}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-[12px] font-semibold ${c.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>
                    {(name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-semibold truncate">{name}</div>
                    <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                      {c.emisor?.nit && <span className="font-mono">{c.emisor.nit}</span>}
                      {c.is_default && <span className="text-[9px] text-slate-400">· default</span>}
                    </div>
                  </div>
                  {c.active && <Check className="w-4 h-4 text-emerald-600 shrink-0" strokeWidth={2.5} />}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => { setOpen(false); setShowCreate(true); }}
            className="w-full flex items-center gap-2 px-3 py-2.5 border-t border-slate-100 text-[12px] font-medium text-slate-700 hover:bg-slate-50 transition"
            data-testid="company-add-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Crear nueva empresa
          </button>
        </div>
      )}

      {showCreate && createPortal(
        <div className="fixed inset-0 z-[9999] bg-slate-900/55 flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={() => setShowCreate(false)} data-testid="company-create-backdrop">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="company-create-modal">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h2 className="font-heading text-lg flex items-center gap-2"><Building2 className="w-5 h-5" />Nueva empresa</h2>
              <button onClick={() => setShowCreate(false)} className="p-1 rounded hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <Field label="Razón social *">
                <input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} className="cli-inp" data-testid="company-form-nombre" autoFocus />
              </Field>
              <Field label="Alias (visible en el switcher)">
                <input value={form.alias} onChange={e => setForm(f => ({ ...f, alias: e.target.value }))} placeholder="Ej: Empresa #2" className="cli-inp" data-testid="company-form-alias" />
              </Field>
              <Field label="Nombre comercial">
                <input value={form.nombreComercial} onChange={e => setForm(f => ({ ...f, nombreComercial: e.target.value }))} className="cli-inp" data-testid="company-form-nombrecomercial" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="NIT"><input value={form.nit} onChange={e => setForm(f => ({ ...f, nit: e.target.value }))} className="cli-inp font-mono" data-testid="company-form-nit" /></Field>
                <Field label="NRC"><input value={form.nrc} onChange={e => setForm(f => ({ ...f, nrc: e.target.value }))} className="cli-inp font-mono" data-testid="company-form-nrc" /></Field>
              </div>
              <p className="text-[11px] text-slate-400 italic">
                Esta empresa tendrá su propio emisor, certificado y configuración independiente.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-100 text-[13px]">Cancelar</button>
              <button onClick={create} disabled={creating} data-testid="company-form-create"
                className="px-5 py-2 rounded-xl bg-slate-900 text-white text-[13px] font-medium hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-2">
                {creating ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Creando…</> : <>Crear y entrar</>}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <style>{`
        .cli-inp {
          width: 100%; padding: 0.5rem 0.75rem;
          background: white; border-radius: 0.625rem;
          font-size: 12.5px; outline: none;
          box-shadow: inset 0 0 0 1px rgb(226 232 240);
          transition: box-shadow 120ms;
        }
        .cli-inp:focus { box-shadow: inset 0 0 0 2px rgb(15 23 42); }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
